/** The four edges of a box on the page, as `getBoundingClientRect` reports them. */
export interface Box {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * The part of the canvas the arena is drawn into, as shares of the canvas: `x` and `y` from its
 * top left corner, the way the page measures, then the frame's width and height.
 */
export interface ArenaFrame {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const WHOLE_CANVAS: ArenaFrame = { x: 0, y: 0, width: 1, height: 1 };

/** Breathing room between the arena's frame and whatever is drawn over the canvas. */
const CLEARANCE = 8;

const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));

/**
 * The largest part of the canvas that nothing is drawn over. The battle panel, the touch controls
 * and the floating Sound and VR controls all sit over the canvas, and the arena has to stay whole
 * behind them in a desktop window as much as on a phone held upright. Every rectangle bounded by
 * the canvas and the covers' edges is a candidate; the one the arena is drawn largest in wins,
 * and among frames that show it alike, the roomier one. The arena fits the frame's shorter side,
 * so that side counts twice and the area once.
 */
export function freeFrame(canvas: Box, covers: readonly Box[]): ArenaFrame {
  const width = canvas.right - canvas.left;
  const height = canvas.bottom - canvas.top;
  if (width <= 0 || height <= 0) return WHOLE_CANVAS;
  const boxes = covers
    // Something hidden measures as nothing, and nothing needs no clearance.
    .filter((box) => box.right > box.left && box.bottom > box.top)
    .map((box) => ({
      left: clamp(box.left - CLEARANCE - canvas.left, 0, width),
      right: clamp(box.right + CLEARANCE - canvas.left, 0, width),
      top: clamp(box.top - CLEARANCE - canvas.top, 0, height),
      bottom: clamp(box.bottom + CLEARANCE - canvas.top, 0, height),
    }))
    .filter((box) => box.right > box.left && box.bottom > box.top);
  if (boxes.length === 0) return WHOLE_CANVAS;
  const ascending = (a: number, b: number) => a - b;
  const xs = [...new Set([0, width, ...boxes.flatMap((b) => [b.left, b.right])])].sort(ascending);
  const ys = [...new Set([0, height, ...boxes.flatMap((b) => [b.top, b.bottom])])].sort(ascending);
  let best: ArenaFrame | null = null;
  let bestScore = 0;
  for (let i = 0; i < xs.length; i++)
    for (let j = i + 1; j < xs.length; j++)
      for (let k = 0; k < ys.length; k++)
        for (let l = k + 1; l < ys.length; l++) {
          const left = xs[i]!;
          const right = xs[j]!;
          const top = ys[k]!;
          const bottom = ys[l]!;
          const covered = boxes.some(
            (b) => b.left < right && b.right > left && b.top < bottom && b.bottom > top,
          );
          if (covered) continue;
          const w = right - left;
          const h = bottom - top;
          const score = Math.min(w, h) ** 2 * w * h;
          if (score > bestScore) {
            bestScore = score;
            best = { x: left / width, y: top / height, width: w / width, height: h / height };
          }
        }
  return best ?? WHOLE_CANVAS;
}

/** Where the stick's ring last rested, while a thumb holds it somewhere else. */
let restingRing: Box | null = null;

/**
 * Reads the covers off the page: the battle panel, the floating controls and the touch controls.
 * The stick's ring counts where it rests. Held, it follows the thumb, and a frame that jumped with
 * every push would be worse than one it covers; so while it is held, the place it rests in still
 * counts, and pressing or letting go of the stick moves nothing (client-arena:quality#8).
 */
export function measureArenaFrame(canvas: HTMLCanvasElement): ArenaFrame {
  const covers: Box[] = [
    ...document.querySelectorAll(
      '.overlay.battle .panel, .persistent-controls, .touch-controls .touch-buttons',
    ),
  ].map((element) => element.getBoundingClientRect());
  const zone = document.querySelector('.touch-controls .touch-stick-zone');
  const ring = zone?.querySelector('.touch-stick-ring');
  if (!ring || !zone) restingRing = null;
  else if (!zone.classList.contains('held')) restingRing = ring.getBoundingClientRect();
  if (restingRing) covers.push(restingRing);
  return freeFrame(canvas.getBoundingClientRect(), covers);
}
